import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageViewer } from '../../design/ImageViewer'
import { Button, Card, CardBody, formatEventRange } from '@structural-climbing/ui'
import type { NewsItem } from '@structural-climbing/ui'
import { isNewsNew } from './useNews'
import { AttendeeAvatars } from './AttendeeAvatars'
import { NavDot } from '../nav/NavDot'

export interface JoinHandlers {
  onJoin: (id: string) => Promise<{ ok: boolean; full?: boolean }>
  onLeave: (id: string) => Promise<void>
}

// The card body shared by the Events page and the News page: image, chips,
// title, description and, for events, the time range.
export function NewsCardInner({ item, isNext }: { item: NewsItem; isNext?: boolean }) {
  const { t } = useTranslation()
  const [viewing, setViewing] = useState(false)
  const timeLabel = item.is_event ? formatEventRange(item.starts_at, item.ends_at) : ''
  return (
    <CardBody className="py-4">
      <div className="flex items-start gap-4">
        {item.image_url && (
          // Tapping the picture shows it full screen. The click stops here, so a
          // card that links out does not navigate as well.
          <span
            className="shrink-0"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <Button
              isIconOnly
              disableRipple
              aria-label={t('news.openImage')}
              onPress={() => setViewing(true)}
              className="h-16 w-16 overflow-hidden rounded-md bg-content2 p-0"
            >
              <img src={item.image_url} alt="" className="h-full w-full object-cover" />
            </Button>
          </span>
        )}
        <div className="min-w-0 flex-1">
          {/* The date stays on one line; chips wrap under it on narrow screens. */}
          <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="whitespace-nowrap text-xs text-foreground/40">{item.news_date}</p>
            {isNext && (
              <span className="shrink-0 rounded-full bg-warning px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                {t('news.nextBadge')}
              </span>
            )}
            {item.is_event && (
              <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                {t('news.eventBadge')}
              </span>
            )}
            {item.is_app_update && (
              <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                {t('news.appUpdateBadge')}
              </span>
            )}
            {isNewsNew(item.news_date) && (
              <span className="shrink-0 rounded-full bg-danger px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                {t('news.newBadge')}
              </span>
            )}
          </div>
          <p className="font-semibold">{item.title}</p>
          <p className="whitespace-pre-line text-sm text-foreground/70">{item.description}</p>
          {timeLabel && (
            <p className="mt-1.5 whitespace-nowrap text-xs text-foreground/60">🕑 {timeLabel}</p>
          )}
        </div>
      </div>
      <ImageViewer
        src={viewing ? item.image_url : null}
        alt={item.title}
        onClose={() => setViewing(false)}
      />
    </CardBody>
  )
}

export function EventCard({
  item,
  isNext,
  onJoin,
  onLeave,
}: { item: NewsItem; isNext?: boolean } & JoinHandlers) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [full, setFull] = useState(false)

  const seatsLeft = item.seats != null ? item.seats - (item.signup_count ?? 0) : null
  const isFull = full || (seatsLeft !== null && seatsLeft <= 0)

  async function toggle() {
    setBusy(true)
    if (item.joined) {
      await onLeave(item.id)
    } else {
      const res = await onJoin(item.id)
      if (res.full) setFull(true)
    }
    setBusy(false)
  }

  return (
    <Card className={isNext ? 'ring-2 ring-warning/50' : undefined}>
      <NewsCardInner item={item} isNext={isNext} />
      <CardBody className="flex flex-row flex-wrap items-center gap-3 pt-0">
        <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/60">
          <AttendeeAvatars attendees={item.attendees} count={item.signup_count ?? 0} />
          {seatsLeft !== null && (
            <span>{isFull ? t('news.eventFull') : t('news.seatsLeft', { count: seatsLeft })}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {item.link && (
            <Button
              as="a"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="flat"
            >
              {t('news.details')}
            </Button>
          )}
          {item.joined ? (
            <Button size="sm" variant="flat" color="success" isLoading={busy} onPress={toggle}>
              {t('news.leaveEvent')}
            </Button>
          ) : (
            <Button size="sm" color="primary" isLoading={busy} isDisabled={isFull} onPress={toggle}>
              {isFull ? t('news.eventFull') : t('news.joinEvent')}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

export function NewsCard({
  item,
  isNext,
  join,
}: {
  item: NewsItem
  isNext?: boolean
  join?: JoinHandlers
}) {
  // A joinable gym event gets the RSVP button (not a whole-card link). The
  // gym_id check is deliberate: only gym events can be joined, and a global item
  // flagged rsvp_enabled would otherwise render a button that always 404s.
  if (item.is_event && item.rsvp_enabled && item.gym_id && join) {
    return <EventCard item={item} isNext={isNext} onJoin={join.onJoin} onLeave={join.onLeave} />
  }

  // Otherwise the whole item is clickable when it has a link — opens in a new tab.
  // (Covers plain news and non-joinable events that link out, e.g. to Instagram.)
  if (item.link) {
    return (
      <Card
        as="a"
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        isPressable
        className={isNext ? 'w-full ring-2 ring-warning/50' : 'w-full'}
      >
        <NewsCardInner item={item} isNext={isNext} />
      </Card>
    )
  }

  return (
    <Card className={isNext ? 'ring-2 ring-warning/50' : undefined}>
      <NewsCardInner item={item} isNext={isNext} />
    </Card>
  )
}

/**
 * Tab label carrying the same dot the nav uses.
 *
 * It was a red count. The nav decided against numbers everywhere else — three
 * unread posts is not a queue you work through — and a tab strip sitting two
 * rows under a dotted pill saying the same thing in a different alphabet read as
 * two separate warnings. Same mark, same colour, one meaning.
 */
export function TabTitle({ label, dot }: { label: string; dot: boolean }) {
  return (
    <span className="flex items-center gap-2">
      {label}
      {dot && <NavDot kind="new" />}
    </span>
  )
}
